import { observer } from "mobx-react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { LOGO_BLACK } from "../../assets";
import Order from "../../interfaces_and_types/orderType";
import { useRootStore } from "../../stores";
import "./styles.css";

interface IOrderTicket {
  order: Order;
}

const parseDurationMs = (time: string | null | undefined): number | null => {
  if (time === null || time === undefined) return null;
  const match = /^(\d+):([0-5]?\d):([0-5]?\d)/.exec(time.trim());
  if (match === null) return null;
  return (
    (parseInt(match[1], 10) * 3600 +
      parseInt(match[2], 10) * 60 +
      parseInt(match[3], 10)) *
    1000
  );
};

const pad = (num: number) => (num < 10 ? "0" + num : String(num));

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return (
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    "  " +
    pad(date.getDate()) +
    "/" +
    pad(date.getMonth() + 1) +
    "/" +
    date.getFullYear()
  );
};

const formatClock = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return pad(date.getHours()) + ":" + pad(date.getMinutes());
};

const money = (amount: number) => `${amount.toFixed(1)}₪`;

const OrderTicket = forwardRef<HTMLDivElement | null, IOrderTicket>(
  (props, ref) => {
    const place = useRootStore().rootStore.placeStore.place;
    const { t, i18n } = useTranslation();
    const order = props.order;

    const prepareByClock = (() => {
      if (order.prepareBy) {
        const clock = formatClock(order.prepareBy);
        if (clock) return clock;
      }
      const durationMs = parseDurationMs(
        order.estimatedTime || order.timeToPrepare,
      );
      const placed = new Date(order.orderDate).getTime();
      if (durationMs === null || Number.isNaN(placed)) return "";
      return formatClock(new Date(placed + durationMs).toISOString());
    })();

    const itemsTotal = Number(order.price ?? 0);
    const discount = Math.max(0, Number(order.subtotalDiscount ?? 0));
    const finalPrice = Math.max(0, itemsTotal - discount);
    const notes = (order.orderNotes ?? "").trim();

    const contactName = order.isDelivery
      ? [order.deliverBy?.first_name, order.deliverBy?.last_name]
          .filter(Boolean)
          .join(" ")
      : order.orderer;
    const contactPhone = order.isDelivery
      ? (order.deliverBy?.mobile ?? "")
      : (order.ordererMobile ?? "");

    return (
      <div
        ref={ref}
        className="ticket-print"
        style={{
          fontFamily:
            i18n.language === "ar"
              ? "'Cairo', sans-serif"
              : "'Rubik', sans-serif",
          direction: i18n.language === "en" ? "ltr" : "rtl",
        }}
      >
        <img className="ticket-logo" src={LOGO_BLACK} alt="Yala" />
        {place?.title ? (
          <div className="ticket-place">{t(place.title)}</div>
        ) : null}

        <div className="ticket-dash" />

        <div className="ticket-strong">{order.id}</div>
        <div className="ticket-plain">{formatDateTime(order.orderDate)}</div>
        {prepareByClock ? (
          <div className="ticket-plain">
            {t("Prepare this order by")} {prepareByClock}
          </div>
        ) : null}

        <div className="ticket-dash" />

        {order.isNewCustomer ? (
          <>
            <div className="ticket-strong">{t("New customer")}</div>
            <div className="ticket-plain">
              {t("Make his first order special")}
            </div>
            <div className="ticket-dash" />
          </>
        ) : null}

        {notes ? (
          <>
            <div className="ticket-strong">{t("Customer's note")}</div>
            <div className="ticket-plain">{notes}</div>
            <div className="ticket-dash" />
          </>
        ) : null}

        {(order.items ?? []).map((item) => (
          <div key={item.itemOrderID} className="ticket-item">
            <div className="ticket-strong ticket-item-row">
              <span>{t(item.title)}</span>
              <span>x{item.quantity}</span>
            </div>
            {(item.subItemsCategories ?? []).map((category, index) =>
              category.subItems.length > 0 ? (
                <div
                  key={`${category.id}-${index}`}
                  className="ticket-plain ticket-mods"
                >
                  {t(category.title)}:{" "}
                  {category.subItems
                    .map((subItem) => t(subItem.title))
                    .join(", ")}
                </div>
              ) : null,
            )}
          </div>
        ))}

        <div className="ticket-dash" />

        <div className="ticket-plain">
          {t("Total price")} {money(itemsTotal)}
        </div>
        {discount > 0 ? (
          <div className="ticket-plain">
            {t("Discount")} -{money(discount)}
          </div>
        ) : null}

        <div className="ticket-dash" />

        <div className="ticket-plain">
          {t("Final price")} {money(finalPrice)}
        </div>

        <div className="ticket-dash" />

        <div className="ticket-plain">
          {t(order.payMethod)} {money(finalPrice)}
        </div>

        <div className="ticket-dash" />

        <div className="ticket-plain">
          {order.isDelivery ? t("Delivery by courier") : t("Self pickup")}
        </div>
        <div className="ticket-plain">{t("Contact")}</div>
        {contactName ? <div className="ticket-plain">{contactName}</div> : null}
        {contactPhone ? (
          <div className="ticket-plain">{contactPhone}</div>
        ) : null}

        <div className="ticket-dash" />

        <div className="ticket-plain">{t("Yala wishes you bon appetit")}</div>
        <div className="ticket-tail" />
      </div>
    );
  },
);

export default observer(OrderTicket);
